import type { EvaluatedValue } from '../../eval/types';

/**
 * Render the evaluated value of a calc Characteristic the way the legacy
 * storefront did: locale-aware decimal separator, fixed decimal places.
 *
 * Mirrors `configento/option/type/expression.js`:
 *   parseFloat(result).toFixed(decimals)
 * with the addition of an `Intl.NumberFormat` pass so de_DE renders
 * "1,50" while en_US renders "1.50". Non-numeric results (combi-string
 * concatenation) round-trip as-is; `null` renders as an em dash so the
 * Characteristic isn't a confusing blank line in the UI.
 */
export function formatCalculatedValue(
    value: EvaluatedValue,
    decimalPlace: number,
    locale: string,
): string {
    if (value === null) return '—';
    if (typeof value === 'string') return value;
    if (!Number.isFinite(value)) return '—';

    const digits = Number.isFinite(decimalPlace) && decimalPlace >= 0 ? decimalPlace : 0;
    try {
        return new Intl.NumberFormat(localeForIntl(locale), {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
        }).format(value);
    } catch {
        // Some non-BCP-47 locale slugs (e.g. legacy `de_DE`) trip Intl —
        // fall back to plain JS formatting so the value still renders.
        return value.toFixed(digits);
    }
}

function localeForIntl(locale: string): string {
    // Intl wants `de-DE`, our messages module uses `de_DE`. Swap.
    return locale.replace('_', '-');
}
